/**
 * @file FormSheetTabs.tsx
 * @brief Pill tabs for a long form sheet, with icons, and the panel props.
 *
 * @details
 * Built for Edit user (DES 01-10-2026: "isay tabs main karo too long",
 * "tabs main icons dalo", "form ki height ooper neechay na hoo tab change
 * karnay per"), shared with Edit company ("same look do").
 *
 * - ⭐ Since 08-10-2026 (DES: "baki jaga bhi karoo desktop main, jahan jahan
 *   tabs hain"): the phone's VZTabs look by default (`tone="phone"`): the
 *   tabs in one rounded track, equal widths, a neutral active pill that
 *   glides between them with a slight overshoot. The paragraph below is the
 *   older `tone="brand"`.
 * - Web style (DES 01-10-2026: "different style of tabs for the web", picked
 *   "B wala red filled pill"): the active tab is a fully round soft-red pill
 *   with brand text and icon; the others are plain grey text that gets a
 *   faint grey pill on hover. Lucide icons at 16px. (Mobile keeps the
 *   underline standard.)
 * - Timeline (DES 01-10-2026: "time line style doo keeping the exact look"):
 *   the same pills, joined by a short hairline.
 * - `formSheetTabPanel(active, id)` goes on each tab's wrapper. Hidden
 *   panels are display:none, inert and aria-hidden but stay mounted, so
 *   values and errors survive. The sheet's height follows the open tab
 *   (DES 01-10-2026: "now form height should be dynamic"; earlier the
 *   panels overlapped so the tallest tab set a fixed height).
 *
 * @author DES
 * @date 01-10-2026
 */

import { Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/shared/theme/cn";

export type FormSheetTab<T extends string> = { id: T; label: string; icon: LucideIcon };

export function FormSheetTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  compact = false,
  tone = "phone",
  fitNarrow = false,
}: {
  tabs: FormSheetTab<T>[];
  value: T;
  onChange: (tab: T) => void;
  /** Accessible name of the tab list. */
  label: string;
  /** Sub tabs inside a tab: smaller pills, no side padding, no timeline
   *  lines (DES 01-10-2026: Enterprise in two sub tabs). */
  compact?: boolean;
  /** "phone" = the app's VZTabs look (DES 08-10-2026, Notes Library): the
   *  tabs in one rounded track (#ECEBE9 / dark --app-quaternary), equal widths, the active
   *  one a neutral pill (#E2E0DD light, white 10% dark) with primary text,
   *  not red; 38px, 15px semibold. The default everywhere since DES 08-10-2026
   *  ("baki jaga bhi karoo, jahan jahan tabs hain"); "brand" is the old soft-red
   *  pill, kept for a screen that still needs it. */
  tone?: "brand" | "phone";
  /** Phone tone, many tabs: below 640px the labels shrink, the icons hide and
   *  a long label truncates, so five tabs fit a 390px screen (Profile, W25). */
  fitNarrow?: boolean;
}) {
  const phone = tone === "phone";
  const activeIndex = tabs.findIndex((t) => t.id === value);
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        phone
          ? // The phone's pill track (VZTabs pillContainer): the tabs sit in
            // one rounded track, equal widths, the active one a darker pill.
            "relative inline-grid auto-cols-fr grid-flow-col gap-1 rounded-full bg-[#ECEBE9] p-1.5 dark:bg-app-quaternary"
          : "flex flex-wrap items-center gap-y-1.5",
        !phone && (compact ? "gap-x-1.5" : "px-6 pb-3"),
        // Sheets: the track keeps the old row's inset from the sheet edge.
        phone && !compact && "mx-6 mb-3",
      )}
    >
      {phone && activeIndex >= 0 ? (
        // The active pill GLIDES between tabs with a slight overshoot, the
        // phone's SLIDE_SPRING (damping 22, stiffness 260, mass 0.75) as a
        // CSS curve. Grid columns are equal, so one step is the pill's own
        // width plus the 4px gap; p-1.5 = 6px inset.
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-1.5 left-1.5 top-1.5 rounded-full bg-[#E2E0DD] motion-reduce:!transition-none dark:bg-white/10"
          style={{
            transition: "transform 380ms cubic-bezier(0.3, 1.25, 0.5, 1)",
            width: `calc((100% - 12px - ${(tabs.length - 1) * 4}px) / ${tabs.length})`,
            transform: `translateX(calc(${activeIndex} * (100% + 4px)))`,
          }}
        />
      ) : null}
      {tabs.map((t, i) => {
        const active = t.id === value;
        return (
          <Fragment key={t.id}>
            {/* Timeline: a hairline joins each pill to the next. */}
            {i > 0 && !compact && !phone ? (
              <span aria-hidden className="mx-1 h-px w-6 shrink-0 bg-foreground/15" />
            ) : null}
            <button
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(t.id)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full font-semibold transition-colors",
                phone && "relative z-10 justify-center",
                phone
                  ? fitNarrow
                    ? "h-[38px] min-w-0 px-1.5 text-[12px] sm:px-3.5 sm:text-[15px]"
                    : "h-[38px] px-3.5 text-[15px]"
                  : compact
                    ? "h-8 px-3.5 text-[13px]"
                    : "h-9 px-4 text-[14px]",
                // No ring: the sheet's focus lands on a tab when it opens and a
                // ring read as a stuck border on the pill (DES 01-10-2026).
                "focus-visible:outline-none",
                active
                  ? phone
                    ? "text-foreground"
                    : "bg-brand-soft text-brand"
                  : phone
                    ? "text-muted-foreground hover:text-foreground focus-visible:text-foreground"
                    : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:bg-foreground/5 focus-visible:text-foreground",
              )}
            >
              <t.icon
                className={cn(
                  "h-4 w-4",
                  active && !phone ? "text-brand" : "text-current",
                  fitNarrow && "hidden shrink-0 sm:block",
                )}
                strokeWidth={1.75}
              />
              {fitNarrow ? <span className="truncate">{t.label}</span> : t.label}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

/** Props for one tab's panel; all panels overlap in one grid cell. */
export function formSheetTabPanel<T extends string>(active: T, id: T) {
  const shown = active === id;
  return {
    role: "tabpanel",
    "aria-hidden": !shown,
    inert: !shown,
    // Dynamic height (DES 01-10-2026: "now form height should be dynamic"):
    // a hidden panel is display:none, so the sheet fits the open tab. It
    // stays mounted, so values and errors survive. (Was: all panels
    // overlapped in one grid cell, tallest tab set the height.)
    className: cn("min-w-0", !shown && "hidden"),
  } as const;
}
